// src/components/common/TracabilityInfo.jsx
//
// Bloc de traçabilité réutilisable affichant :
//   - "Créé le X par Y"
//   - "Modifié le X par Y"
//
// Auto-résout les user_id en noms via useUsersMap.
// Tolère les champs absents : si un champ manque, la ligne correspondante
// n'est pas affichée (ou affiche un placeholder discret).
//
// Usage :
//   <TracabilityInfo
//     createdAt={entity.created_at}
//     createdById={entity.created_by_user_id}
//     updatedAt={entity.updated_at}
//     updatedById={entity.updated_by_user_id}
//   />
//
// Variantes :
//   <TracabilityInfo compact ... />   → version inline une ligne
//   <TracabilityInfo dense ... />     → padding réduit

import React from 'react';
import { Clock, UserCircle2, History } from 'lucide-react';
import { useUsersMap } from '../../hooks/useUsersMap';
import './TracabilityInfo.css';

// Formate une date ISO en "8 juin 2026 à 14:23"
function formatDateTime(iso) {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    const datePart = d.toLocaleDateString('fr-FR', {
      day: 'numeric', month: 'short', year: 'numeric',
    });
    const timePart = d.toLocaleTimeString('fr-FR', {
      hour: '2-digit', minute: '2-digit',
    });
    return `${datePart} à ${timePart}`;
  } catch {
    return null;
  }
}

// Renvoie "il y a X" relatif (pour tooltip)
function timeAgo(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const diffMs = Date.now() - d.getTime();
  const sec = Math.floor(diffMs / 1000);
  if (sec < 60)        return 'à l\'instant';
  const min = Math.floor(sec / 60);
  if (min < 60)        return `il y a ${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24)          return `il y a ${h} h`;
  const j = Math.floor(h / 24);
  if (j < 30)          return `il y a ${j} j`;
  const mo = Math.floor(j / 30);
  if (mo < 12)         return `il y a ${mo} mois`;
  return `il y a ${Math.floor(mo / 12)} an${Math.floor(mo / 12) > 1 ? 's' : ''}`;
}

export default function TracabilityInfo({
  createdAt,
  createdById,
  createdByName,    // si déjà dénormalisé côté backend, on l'utilise direct
  updatedAt,
  updatedById,
  updatedByName,
  compact = false,
  dense   = false,
  showWarningIfNoData = false,
}) {
  const { getUser } = useUsersMap();

  const createdDate = formatDateTime(createdAt);
  const updatedDate = formatDateTime(updatedAt);

  // Priorité : résolution via user_id (= vrai user du CRM), fallback texte libre
  const createdUser = (createdById && getUser(createdById)?.name)
    || createdByName
    || null;
  const updatedUser = (updatedById && getUser(updatedById)?.name)
    || updatedByName
    || null;

  // Si rien à afficher : option d'afficher un warning
  const hasAny = createdDate || updatedDate;
  if (!hasAny) {
    if (!showWarningIfNoData) return null;
    return (
      <div className={`tracability-info tracability-info--empty ${dense ? 'tracability-info--dense' : ''}`}>
        <History size={12} />
        <span>Traçabilité indisponible</span>
      </div>
    );
  }

  // ── Mode COMPACT (inline, une seule ligne) ────────────────
  if (compact) {
    return (
      <div className={`tracability-info tracability-info--compact ${dense ? 'tracability-info--dense' : ''}`}>
        {updatedDate ? (
          <span title={timeAgo(updatedAt)}>
            <Clock size={11} />
            Modifié le {updatedDate}
            {updatedUser && <> par <strong>{updatedUser}</strong></>}
          </span>
        ) : createdDate && (
          <span title={timeAgo(createdAt)}>
            <Clock size={11} />
            Créé le {createdDate}
            {createdUser && <> par <strong>{createdUser}</strong></>}
          </span>
        )}
      </div>
    );
  }

  // ── Mode COMPLET (bloc 1-2 lignes) ────────────────────────
  return (
    <div className={`tracability-info ${dense ? 'tracability-info--dense' : ''}`}>
      {createdDate && (
        <div className="tracability-info-row" title={timeAgo(createdAt)}>
          <Clock size={12} className="tracability-info-icon" />
          <span className="tracability-info-label">Créé le</span>
          <span className="tracability-info-value">{createdDate}</span>
          {createdUser && (
            <>
              <UserCircle2 size={12} className="tracability-info-icon tracability-info-icon--user" />
              <span className="tracability-info-user">par {createdUser}</span>
            </>
          )}
        </div>
      )}
      {updatedDate && updatedDate !== createdDate && (
        <div className="tracability-info-row" title={timeAgo(updatedAt)}>
          <History size={12} className="tracability-info-icon" />
          <span className="tracability-info-label">Modifié le</span>
          <span className="tracability-info-value">{updatedDate}</span>
          {updatedUser && (
            <>
              <UserCircle2 size={12} className="tracability-info-icon tracability-info-icon--user" />
              <span className="tracability-info-user">par {updatedUser}</span>
            </>
          )}
        </div>
      )}
    </div>
  );
}
